/**
 * Modello dell'**export** della superficie Statistiche SLA (piano Client §5 F4c,
 * §8 riga 128, §6 riga 90).
 *
 * **Dove nasce il documento: nel client, dai payload gia' resi.** Il contratto P2
 * (`GET /sla/{org}/compliance|trend|drilldown/*`,
 * `backend_statistiche/app/controllers/sla.py:166-287`) non espone alcuna rotta di
 * export: le **20** chiavi `sla.export.*` del catalogo P1
 * (`app/statics/sla_translations/manifest.json`) hanno `renderer: client` e
 * `surface: export`, quindi il documento e' un artefatto del client e non una
 * risposta del backend. Il modulo di export Python
 * (`services/sla/export.py`, test `app/tests/test_sla_export.py`) e' dichiarato
 * **non implementato** in questo checkout e la consegna email e' dichiarata
 * fuori dal repository Python ("La consegna email e il recupero dal cold storage
 * sono fuori da questo repository"): l'export lato client non lo duplica e non lo
 * simula.
 *
 * **Conseguenza per "nessun cambio della base a insaputa dell'utente"** (piano §5
 * F4b): il documento si costruisce **dagli stessi payload** che la superficie sta
 * rendendo (`compliance`, `trend`, `drilldown`) e dagli **stessi filtri** che ha
 * inviato. Non c'e' una seconda richiesta al momento del download, quindi non
 * esiste un percorso in cui l'export diverga da cio' che l'utente vede: i valori
 * dell'export sono quelli dell'ultimo payload reso, e il periodo/timezone/basi/
 * livello/filtri/campione sono scritti nel documento come **metadati**.
 *
 * **Cosa l'export contiene**, con chiavi **esistenti** del catalogo P1 (nessuna
 * chiave nuova, nessun testo inventato):
 * - titolo `sla.export.title`; i due formati `sla.export.format.pdf|excel`;
 * - metadati: `sla.export.header.period|timezone|level|filters|basis|sample_size`
 *   piu' i conteggi fuori denominatore (`sla.statistics.excluded.*`,
 *   `sla.statistics.coverage`, `sla.statistics.average_working_time`) e la nota di
 *   riservatezza del payload (`sla.statistics.privacy_note`): le stesse grandezze
 *   della vista, dichiarate nel documento;
 * - legenda: `sla.export.legend` e `sla.export.legend.escalation`;
 * - tabelle: compliance per base (`sla.statistics.compliance.title`, riusa
 *   `sla.statistics.denominator` e `sla.statistics.compliance.rate`), trend per
 *   base (`sla.statistics.trend.title`, `sla.statistics.period`) e — se aperto —
 *   il drill-down (`sla.statistics.drilldown.title`, `sla.statistics.filter.level`,
 *   `sla.statistics.filter.state`).
 *
 * **Locale corrente**: le percentuali e i numeri sono formattati con la **stessa**
 * funzione che la superficie rende (`slaStatisticsRate`), quindi il documento e la
 * vista mostrano la stessa stringa localizzata: e' la verifica di "filtri e locale
 * correnti" senza doppie verita' sul formato.
 *
 * **Cosa resta fuori, dichiarato e non simulato:**
 * - **allegato email** (`sla.export.email.subject|body|attachment`): nessuna rotta
 *   la produce — il Server espone `GET|PUT /sla/organizations/{org}/settings` e
 *   `GET|PATCH /sla/preferences` (`Controller.SLA.pas:91-105`) ma nessuna rotta di
 *   invio, e la consegna e' dichiarata fuori dal repository Python
 *   (`app/tests/test_sla_export.py:14-18`). La chiave `sla.export.email.attachment`
 *   resta quindi **non resa**: un comando che la offrisse sarebbe inerte.
 * - `sla.export.retention_warning` (`{from}`) e `sla.export.size_limit`: valgono
 *   per il percorso email/cold storage, non per un download locale; il tetto reale
 *   dei dati e' quello dichiarato dal payload (`sample.limit`/`limited`) ed e'
 *   scritto nel documento.
 */

import type {
  SlaComplianceLevel,
  SlaComplianceQuery,
  SlaComplianceResponse,
  SlaComplianceSample,
  SlaDrilldownResponse,
  SlaTrendResponse,
} from "model/slaModel";
import {
  SLA_STATISTICS_ROW_IDENTITY,
  SLA_STATISTICS_TEXT_KEYS,
  slaStatisticsLevelKey,
  slaStatisticsRate,
  slaStatisticsSample,
  type SlaStatisticsRange,
} from "model/slaStatisticsModel";

/** I due formati che il client produce davvero (nessun terzo formato). */
export const SLA_STATISTICS_EXPORT_FORMATS = ["pdf", "excel"] as const;

export type SlaStatisticsExportFormat =
  (typeof SLA_STATISTICS_EXPORT_FORMATS)[number];

/**
 * Estensione del file per formato. `excel` produce un `.xlsx` reale (libreria
 * `xlsx`, gia' in `package.json` e gia' usata da `FilePreviewDialog.tsx`), non un
 * CSV rinominato: le celle numeriche restano numeriche.
 */
export const SLA_STATISTICS_EXPORT_FILE_EXTENSION: Record<
  SlaStatisticsExportFormat,
  string
> = { pdf: "pdf", excel: "xlsx" };

/** Tipo MIME del file prodotto, per il Blob del download. */
export const SLA_STATISTICS_EXPORT_MIME: Record<SlaStatisticsExportFormat, string> = {
  pdf: "application/pdf",
  excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

/**
 * Chiavi del catalogo P1 che **solo** l'export rende: sono le 6 intestazioni dei
 * metadati piu' il titolo, i due formati, le due voci di legenda e il testo di
 * "niente da esportare". Nessuna e' nuova: sono tutte nel manifest P1 con
 * `surface: export`.
 */
export const SLA_EXPORT_TEXT_KEYS = {
  title: "sla.export.title",
  formatPdf: "sla.export.format.pdf",
  formatExcel: "sla.export.format.excel",
  headerPeriod: "sla.export.header.period",
  headerTimezone: "sla.export.header.timezone",
  headerBasis: "sla.export.header.basis",
  headerFilters: "sla.export.header.filters",
  headerLevel: "sla.export.header.level",
  headerSampleSize: "sla.export.header.sample_size",
  legend: "sla.export.legend",
  /** Legenda del trattamento dell'escalation: **un** breach, nessun doppio conteggio. */
  legendEscalation: "sla.export.legend.escalation",
  noData: "sla.export.no_data",
} as const;

/**
 * Chiavi del catalogo P1 che appartengono all'export ma che questa implementazione
 * **non** rende, perche' appartengono al percorso email/allegato, che nessuna
 * rotta produce (vedi il commento di testa). Dichiarate qui perche' il manifest
 * verso Python P1 le contenga comunque e perche' la loro assenza dal DOM sia una
 * scelta verificabile, non una dimenticanza.
 */
export const SLA_STATISTICS_EXPORT_UNUSED_CATALOG_KEYS: string[] = [
  "sla.export.email.attachment",
  "sla.export.email.body",
  "sla.export.email.subject",
  "sla.export.retention_warning",
  "sla.export.size_limit",
];

/**
 * File del client che realizzano l'export: modello (questo file), scrittori e
 * comando. La spec li legge come **contratto statico** (il documento e' un
 * artefatto del client, non una risposta HTTP: non c'e' una rotta da verificare).
 */
export const SLA_STATISTICS_EXPORT_SOURCES = {
  model: "src/model/slaStatisticsExportModel.ts",
  writer: "src/utils/slaStatisticsExport.ts",
  command: "src/components/sla/SlaStatisticsExportMenu.tsx",
} as const;

/**
 * Traduttore passato dal componente: il modello resta puro e non importa il
 * catalogo (`getTranslation` restituisce la chiave quando il testo non e'
 * consegnato, quindi il modello non dipende dalla consegna dei testi).
 */
export type SlaStatisticsExportTranslate = (
  key: string,
  params?: Record<string, string | number>,
) => string;

/** Testi risolti una volta sola: il documento non li rilegge dal catalogo. */
export interface SlaStatisticsExportTexts {
  title: string;
  formatPdf: string;
  formatExcel: string;
  headerPeriod: string;
  headerTimezone: string;
  headerBasis: string;
  headerFilters: string;
  headerLevel: string;
  headerSampleSize: string;
  legend: string;
  legendEscalation: string;
  noData: string;
  complianceTitle: string;
  trendTitle: string;
  drilldownTitle: string;
  denominator: string;
  rate: string;
  rateUnavailable: string;
  basisOriginal: string;
  basisEffective: string;
  excludedCancelled: string;
  excludedInterrupted: string;
  excludedAuthorityTerminated: string;
  excludedNoSla: string;
  coverage: string;
  averageWorkingTime: string;
  privacy: string;
  levelProcess: string;
  levelActivity: string;
  stateLabel: string;
}

export const slaStatisticsExportTexts = (
  translate: SlaStatisticsExportTranslate,
): SlaStatisticsExportTexts => ({
  title: translate(SLA_EXPORT_TEXT_KEYS.title),
  formatPdf: translate(SLA_EXPORT_TEXT_KEYS.formatPdf),
  formatExcel: translate(SLA_EXPORT_TEXT_KEYS.formatExcel),
  headerPeriod: translate(SLA_EXPORT_TEXT_KEYS.headerPeriod),
  headerTimezone: translate(SLA_EXPORT_TEXT_KEYS.headerTimezone),
  headerBasis: translate(SLA_EXPORT_TEXT_KEYS.headerBasis),
  headerFilters: translate(SLA_EXPORT_TEXT_KEYS.headerFilters),
  headerLevel: translate(SLA_EXPORT_TEXT_KEYS.headerLevel),
  headerSampleSize: translate(SLA_EXPORT_TEXT_KEYS.headerSampleSize),
  legend: translate(SLA_EXPORT_TEXT_KEYS.legend),
  legendEscalation: translate(SLA_EXPORT_TEXT_KEYS.legendEscalation),
  noData: translate(SLA_EXPORT_TEXT_KEYS.noData),
  complianceTitle: translate(SLA_STATISTICS_TEXT_KEYS.complianceTitle),
  trendTitle: translate(SLA_STATISTICS_TEXT_KEYS.trendTitle),
  drilldownTitle: translate(SLA_STATISTICS_TEXT_KEYS.drilldownTitle),
  denominator: translate(SLA_STATISTICS_TEXT_KEYS.denominator),
  rate: translate(SLA_STATISTICS_TEXT_KEYS.rate),
  rateUnavailable: translate(SLA_STATISTICS_TEXT_KEYS.rateUnavailable),
  basisOriginal: translate(SLA_STATISTICS_TEXT_KEYS.basisOriginal),
  basisEffective: translate(SLA_STATISTICS_TEXT_KEYS.basisEffective),
  excludedCancelled: translate(SLA_STATISTICS_TEXT_KEYS.excludedCancelled),
  excludedInterrupted: translate(SLA_STATISTICS_TEXT_KEYS.excludedInterrupted),
  excludedAuthorityTerminated: translate(
    SLA_STATISTICS_TEXT_KEYS.excludedAuthorityTerminated,
  ),
  excludedNoSla: translate(SLA_STATISTICS_TEXT_KEYS.excludedNoSla),
  coverage: translate(SLA_STATISTICS_TEXT_KEYS.coverage),
  averageWorkingTime: translate(SLA_STATISTICS_TEXT_KEYS.averageWorkingTime),
  privacy: translate(SLA_STATISTICS_TEXT_KEYS.privacy),
  levelProcess: translate(slaStatisticsLevelKey("process")),
  levelActivity: translate(slaStatisticsLevelKey("task")),
  stateLabel: translate(SLA_STATISTICS_TEXT_KEYS.stateLabel),
});

/** Cella del documento: i conteggi restano **numeri**, le percentuali sono localizzate. */
export type SlaStatisticsExportCell = string | number;

export interface SlaStatisticsExportTable {
  /** Chiave di catalogo del titolo della tabella (mai prosa costruita a mano). */
  titleKey: string;
  title: string;
  columns: string[];
  rows: SlaStatisticsExportCell[][];
}

export interface SlaStatisticsExportMetadataRow {
  /** Chiave di catalogo della label: il documento non inventa didascalie. */
  key: string;
  label: string;
  value: SlaStatisticsExportCell;
}

export interface SlaStatisticsExportDocument {
  /** false = nessun payload da esportare: il comando lo **dichiara**, non tace. */
  available: boolean;
  /** Chiave di catalogo del motivo quando `available` e' false (o `null`). */
  errorKey: string | null;
  errorText: string | null;
  title: string;
  metadata: SlaStatisticsExportMetadataRow[];
  legend: string[];
  tables: SlaStatisticsExportTable[];
  /** Nome del file: stesso slug del pattern export esistente (`utils/processExport.ts`). */
  fileName: string;
}

export interface SlaStatisticsExportInput {
  texts: SlaStatisticsExportTexts;
  locale: string;
  /** Periodo `[from, to)` dichiarato e inviato dalla superficie. */
  period: SlaStatisticsRange;
  level: SlaComplianceLevel;
  /** Riepilogo dei filtri inviati, in forma di query string (nessuna prosa). */
  filters: string;
  compliance: SlaComplianceResponse | null;
  trend: SlaTrendResponse | null;
  drilldown: SlaDrilldownResponse | null;
  /** Nota di riservatezza del payload (`sla.statistics.privacy_note`). */
  privacyNote: string | null;
  format: SlaStatisticsExportFormat;
}

/**
 * Riepilogo dei filtri in forma di query string: le **stesse** chiavi che la
 * superficie invia al contratto P2 (`modelids`, `customervalues`), nell'ordine
 * dichiarato. Nessuna prosa e nessuna label: il documento dichiara con che filtro
 * e' stato prodotto, non lo interpreta. Lista vuota -> stringa vuota (resa "—").
 */
export const slaStatisticsExportFilterSummary = (
  query: Partial<SlaComplianceQuery>,
): string =>
  (["modelids", "customervalues"] as const)
    .filter((key) => {
      const value = query[key];
      return typeof value === "string" && value.length > 0;
    })
    .map((key) => `${key}=${query[key]}`)
    .join("&");

/** Percentuale localizzata con la **stessa** regola della vista (N/D incluso). */
const rateText = (
  input: SlaStatisticsExportInput,
  value: number | null | undefined,
  eligible: number,
): string =>
  slaStatisticsRate(value, eligible, input.locale, input.texts.rateUnavailable)
    .text;

const sampleRow = (
  input: SlaStatisticsExportInput,
  sample: SlaComplianceSample | null | undefined,
  key: string,
  label: string,
): SlaStatisticsExportMetadataRow => {
  const view = slaStatisticsSample(sample);
  return { key, label, value: view.available ? view.text : "—" };
};

/**
 * Documento di export dai payload correnti.
 *
 * Puro: nessuna richiesta, nessun accesso al DOM, nessuna lettura dell'orologio
 * (periodo, `as_of` e campione arrivano dal payload). Se la conformita' non e'
 * stata resa (`compliance === null`, perche' il periodo e' oltre il limite o la
 * richiesta e' fallita) il documento e' **non disponibile** con la chiave
 * `sla.export.no_data`: il comando mostra quel motivo invece di produrre un file
 * vuoto.
 */
export const slaStatisticsExportDocument = (
  input: SlaStatisticsExportInput,
): SlaStatisticsExportDocument => {
  const { texts, compliance, trend, drilldown, period, level } = input;
  const fileName = slaStatisticsExportFileName(texts.title, input.format, period);

  if (!compliance) {
    return {
      available: false,
      errorKey: SLA_EXPORT_TEXT_KEYS.noData,
      errorText: texts.noData,
      title: texts.title,
      metadata: [],
      legend: [],
      tables: [],
      fileName,
    };
  }

  const metadata: SlaStatisticsExportMetadataRow[] = [
    {
      key: SLA_EXPORT_TEXT_KEYS.headerPeriod,
      label: texts.headerPeriod,
      value: `${period.from} → ${period.to}`,
    },
    {
      key: SLA_EXPORT_TEXT_KEYS.headerTimezone,
      label: texts.headerTimezone,
      // Timezone **del payload** (mai quella del browser, piano §5 F4a).
      value: compliance.timezone,
    },
    {
      key: SLA_EXPORT_TEXT_KEYS.headerLevel,
      label: texts.headerLevel,
      value: level === "task" ? texts.levelActivity : texts.levelProcess,
    },
    {
      key: SLA_EXPORT_TEXT_KEYS.headerFilters,
      label: texts.headerFilters,
      value: input.filters.length > 0 ? input.filters : "—",
    },
    {
      key: SLA_EXPORT_TEXT_KEYS.headerBasis,
      label: texts.headerBasis,
      // Entrambe le basi, dichiarate: nessuna "base combinata" e nessuna scelta muta.
      value: `${texts.basisOriginal}, ${texts.basisEffective}`,
    },
    sampleRow(
      input,
      compliance.sample,
      SLA_EXPORT_TEXT_KEYS.headerSampleSize,
      texts.headerSampleSize,
    ),
    // Esiti **fuori dal denominatore**: separati, mai sommati alla compliance.
    {
      key: SLA_STATISTICS_TEXT_KEYS.excludedCancelled,
      label: texts.excludedCancelled,
      value: compliance.excluded.cancelled,
    },
    {
      key: SLA_STATISTICS_TEXT_KEYS.excludedInterrupted,
      label: texts.excludedInterrupted,
      value: compliance.excluded.interrupted,
    },
    {
      key: SLA_STATISTICS_TEXT_KEYS.excludedAuthorityTerminated,
      label: texts.excludedAuthorityTerminated,
      value: compliance.excluded.authority_terminated,
    },
    {
      key: SLA_STATISTICS_TEXT_KEYS.excludedNoSla,
      label: texts.excludedNoSla,
      value: compliance.without_sla,
    },
    {
      key: SLA_STATISTICS_TEXT_KEYS.coverage,
      label: texts.coverage,
      value:
        compliance.coverage_percent === null
          ? texts.rateUnavailable
          : new Intl.NumberFormat(input.locale, {
              style: "percent",
              maximumFractionDigits: 2,
            }).format(compliance.coverage_percent / 100),
    },
    {
      key: SLA_STATISTICS_TEXT_KEYS.averageWorkingTime,
      label: texts.averageWorkingTime,
      value:
        compliance.average_working_seconds === null
          ? texts.rateUnavailable
          : String(compliance.average_working_seconds),
    },
    // Nessuna metrica per persona: la nota del payload viaggia col documento.
    {
      key: SLA_STATISTICS_TEXT_KEYS.privacy,
      label: texts.privacy,
      value: input.privacyNote ?? "—",
    },
  ];

  const tables: SlaStatisticsExportTable[] = [
    {
      titleKey: SLA_STATISTICS_TEXT_KEYS.complianceTitle,
      title: texts.complianceTitle,
      columns: [texts.headerBasis, texts.denominator, texts.rate],
      rows: [
        [
          texts.basisOriginal,
          compliance.original.eligible,
          rateText(input, compliance.original.percent, compliance.original.eligible),
        ],
        [
          texts.basisEffective,
          compliance.effective.eligible,
          rateText(input, compliance.effective.percent, compliance.effective.eligible),
        ],
      ],
    },
  ];

  if (trend) {
    tables.push({
      titleKey: SLA_STATISTICS_TEXT_KEYS.trendTitle,
      title: texts.trendTitle,
      columns: [texts.headerPeriod, texts.headerBasis, texts.denominator, texts.rate],
      rows: trend.data.flatMap((point) => [
        [
          point.bucket,
          texts.basisOriginal,
          point.original.eligible,
          rateText(input, point.original.percent, point.original.eligible),
        ],
        [
          point.bucket,
          texts.basisEffective,
          point.effective.eligible,
          rateText(input, point.effective.percent, point.effective.eligible),
        ],
      ]),
    });
  }

  if (drilldown && drilldown.data.length > 0) {
    tables.push({
      titleKey: SLA_STATISTICS_TEXT_KEYS.drilldownTitle,
      title: texts.drilldownTitle,
      columns: [
        SLA_STATISTICS_ROW_IDENTITY,
        texts.headerLevel,
        texts.stateLabel,
        texts.headerPeriod,
      ],
      rows: drilldown.data.map((row) => [
        row.sla_instance_id,
        row.target_type === 2 ? texts.levelActivity : texts.levelProcess,
        row.completion_kind,
        row.closed_at,
      ]),
    });
  }

  return {
    available: true,
    errorKey: null,
    errorText: null,
    title: texts.title,
    metadata,
    legend: [texts.legend, texts.legendEscalation],
    tables,
    fileName,
  };
};

/**
 * Nome del file: slug del titolo localizzato + periodo `[from, to)` + estensione
 * del formato, con lo **stesso** pattern dell'export esistente
 * (`utils/processExport.ts:176-180`: slug normalizzato, data, estensione). Il
 * periodo sta nel nome perche' due export di periodi diversi non si sovrappongano.
 */
export const slaStatisticsExportFileName = (
  title: string,
  format: SlaStatisticsExportFormat,
  range: SlaStatisticsRange,
): string => {
  const slug =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "") || "sla-statistics";
  return `${slug}-${range.from}-${range.to}.${SLA_STATISTICS_EXPORT_FILE_EXTENSION[format]}`;
};
